import React, { useRef, useState } from 'react';
import config from '../Config';
import { authFetch } from '../functions/authFetch';
import { getCookieToken, isJwtExpired } from '../functions/authSession';
import { SUPPORT_EMAIL } from '../legalInfo';
import Turnstile, { turnstileEnabled } from '../components/Turnstile';
import './pages.css';

const TOPICS = [
  'General Inquiry',
  'Billing & Pricing',
  'Technical Support',
  'Container Registry (MCR)',
  'Dedicated API',
  'Other',
];

// A genuine visitor cannot read, understand and fill this form faster than this. Only
// applied to anonymous senders, who are the ones worth screening.
const MIN_FILL_SECONDS = 3;

/** Signed-in callers are identified by their token; the same test ProtectedRoute uses. */
function hasSession() {
  const token = getCookieToken();
  return Boolean(token) && !isJwtExpired(token);
}

export default function ContactUs() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  // Anonymous senders only: signed-in ones are identified by their token.
  const [email, setEmail] = useState('');
  const [botToken, setBotToken] = useState('');
  // Honeypot: hidden from real users, so anything here means an automated fill.
  const [companyWebsite, setCompanyWebsite] = useState('');
  const openedAt = useRef(Date.now());
  const botRef = useRef(null);

  // Read once on mount so the form cannot swap shape underneath someone mid-message.
  const [signedIn] = useState(hasSession);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return;

    if (!signedIn) {
      if (!email) return;
      if ((Date.now() - openedAt.current) / 1000 < MIN_FILL_SECONDS) {
        setStatus('error');
        setErrorMsg('That was too quick — please take a moment and try again.');
        return;
      }
      if (turnstileEnabled() && !botToken) {
        setStatus('error');
        setErrorMsg('Please complete the verification challenge below.');
        return;
      }
    }

    setLoading(true);
    setStatus(null);
    setErrorMsg('');

    try {
      // Signed in, the sender address comes from the token and is never sent in the
      // body; anonymous, it has to be supplied along with a solved challenge.
      const url = `${config.AUTH_API}/v1/contact-us`;
      const init = {
        method: 'POST',
        headers: { accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject,
          message,
          company_website: companyWebsite,
          ...(signedIn ? {} : { email, turnstile_token: botToken }),
        }),
      };
      const res = signedIn ? await authFetch(url, init) : await fetch(url, init);
      if (res.ok) {
        setStatus('success');
        setSubject('');
        setMessage('');
        setEmail('');
      } else {
        const data = await res.json().catch(() => ({}));
        setStatus('error');
        setErrorMsg(
          typeof data.detail === 'string'
            ? data.detail
            : 'Something went wrong. Please try again.'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    } finally {
      // Turnstile tokens are single use, so a retry needs a whole new challenge.
      botRef.current?.reset();
      setLoading(false);
    }
  };

  return (
    <div className="cu-page">

      {/* ── Hero ── */}
      <div className="cu-hero">
        <span className="cu-badge">Get in Touch</span>
        <h1 className="cu-hero-title">We'd love to hear from you</h1>
        <p className="cu-hero-sub">
          Have a question about pricing, the API, or the container registry?<br />
          Send us a message and we'll get back to you shortly.
        </p>
      </div>

      {/* ── Body: info cards + form ── */}
      <div className="cu-body">

        {/* Left: info cards */}
        <div className="cu-info">
          <div className="cu-info-card">
            <div className="cu-info-icon">✉</div>
            <div>
              <div className="cu-info-label">Email</div>
              <div className="cu-info-value">
                <a href={`mailto:${SUPPORT_EMAIL}`} className="cu-link">{SUPPORT_EMAIL}</a>
              </div>
            </div>
          </div>
          <div className="cu-info-card">
            <div className="cu-info-icon">⚡</div>
            <div>
              <div className="cu-info-label">Response time</div>
              <div className="cu-info-value">Usually within 24 hours</div>
            </div>
          </div>
          <div className="cu-info-card">
            <div className="cu-info-icon">📖</div>
            <div>
              <div className="cu-info-label">Documentation</div>
              <div className="cu-info-value">
                Check our <a href="/documentation" className="cu-link">docs</a> for quick answers
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <form className="cu-form" onSubmit={handleSubmit}>
          {!signedIn && (
            <div className="cu-form-group">
              <label className="cu-label">Your Email</label>
              <input
                type="email"
                className="cu-input"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
          )}

          <div className="cu-form-group">
            <label className="cu-label">Topic</label>
            <select
              className="cu-input cu-select"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            >
              <option value="">Select a topic…</option>
              {TOPICS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          <div className="cu-form-group">
            <label className="cu-label">Message</label>
            <textarea
              className="cu-input cu-textarea"
              placeholder="Describe your question or issue in detail…"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          {/* Honeypot. Hidden from users and skipped by screen readers, but a bot
              that fills every input will trip it. */}
          <div className="cu-honeypot" aria-hidden="true">
            <label htmlFor="company_website">Company website</label>
            <input
              type="text"
              id="company_website"
              name="company_website"
              tabIndex={-1}
              autoComplete="off"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
            />
          </div>

          {!signedIn && (
            <Turnstile ref={botRef} onToken={setBotToken} action="contact" />
          )}

          {status === 'success' && (
            <div className="cu-alert cu-alert-success">
              Message sent! We'll be in touch soon.
            </div>
          )}
          {status === 'error' && (
            <div className="cu-alert cu-alert-error">
              {errorMsg || 'Something went wrong. Please try again.'}
            </div>
          )}

          <button
            type="submit"
            className="cu-submit-btn"
            disabled={loading}
          >
            {loading ? 'Sending…' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}
